import React, { useState } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  ChartLine, ChartBar, Table, TrendUp, Lightning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CHART_TYPES = [
  { value: 'line', label: 'Line' },
  { value: 'bar', label: 'Bar' },
  { value: 'area', label: 'Area' },
  { value: 'scatter', label: 'Scatter' },
];

const COLORS = ['#818CF8', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#22D3EE', '#FB923C', '#E879F9'];

const DataAnalysisPage = () => {
  const [activeTab, setActiveTab] = useState('growth');
  const [csvData, setCsvData] = useState('');
  const [growthData, setGrowthData] = useState({ time: '', od: '' });
  const [growthResults, setGrowthResults] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(false);
  // Axis control
  const [allHeaders, setAllHeaders] = useState([]);
  const [xAxis, setXAxis] = useState('');
  const [yAxes, setYAxes] = useState([]);
  const [xLabel, setXLabel] = useState('');
  const [yLabel, setYLabel] = useState('');

  const parseCSV = (csv) => {
    const lines = csv.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row = {};
      headers.forEach((h, idx) => {
        const val = parseFloat(values[idx]);
        row[h] = isNaN(val) ? values[idx]?.trim() : val;
      });
      data.push(row);
    }
    return { headers, data };
  };

  const handleGrowthAnalysis = async () => {
    try {
      const timePoints = growthData.time.split(',').map(t => parseFloat(t.trim())).filter(t => !isNaN(t));
      const odValues = growthData.od.split(',').map(o => parseFloat(o.trim())).filter(o => !isNaN(o));
      if (timePoints.length !== odValues.length || timePoints.length < 3) {
        toast.error('Please enter matching time and OD values (at least 3 points)');
        return;
      }
      setLoading(true);
      const result = await api.analyzeGrowthCurve(timePoints, odValues);
      setGrowthResults(result);
      const data = timePoints.map((t, i) => ({ time: t, OD600: odValues[i] }));
      setChartData(data);
      setAllHeaders(['time', 'OD600']);
      setXAxis('time');
      setYAxes(['OD600']);
      setXLabel('Time (hours)');
      setYLabel('OD600');
      setChartType('line');
      toast.success('Analysis complete');
    } catch {
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVVisualize = () => {
    if (!csvData.trim()) { toast.error('Please paste CSV data'); return; }
    try {
      const { headers, data } = parseCSV(csvData);
      if (headers.length < 2) { toast.error('CSV must have at least 2 columns'); return; }
      setChartData(data);
      setAllHeaders(headers);
      setXAxis(headers[0]);
      setYAxes(headers.slice(1));
      setXLabel(headers[0]);
      setYLabel('');
      toast.success('Data loaded — configure axes below');
    } catch {
      toast.error('Failed to parse CSV');
    }
  };

  const toggleYAxis = (key) => {
    setYAxes(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const tooltipStyle = {
    background: '#1E293B', border: '1px solid rgba(148,163,184,0.15)',
    borderRadius: '8px', color: '#CBD5E1', fontSize: '0.75rem'
  };
  const axisStyle = { stroke: '#475569', fontSize: 11, fill: '#94A3B8' };

  const renderChart = () => {
    if (!chartData || chartData.length === 0 || yAxes.length === 0) return null;
    const margin = { top: 20, right: 30, left: 20, bottom: 30 };
    const xKey = xAxis || Object.keys(chartData[0])[0];

    const xAxisEl = <XAxis dataKey={xKey} {...axisStyle} label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -10, fill: '#94A3B8', fontSize: 12 } : undefined} />;
    const yAxisEl = <YAxis {...axisStyle} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', offset: 0, fill: '#94A3B8', fontSize: 12 } : undefined} />;
    const grid = <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />;
    const tip = <Tooltip contentStyle={tooltipStyle} />;
    const leg = <Legend wrapperStyle={{ fontSize: '0.75rem', color: '#94A3B8' }} />;

    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={chartData} margin={margin}>
            {grid}{xAxisEl}{yAxisEl}{tip}{leg}
            {yAxes.map((key, i) => <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
          </BarChart>
        );
      case 'area':
        return (
          <AreaChart data={chartData} margin={margin}>
            {grid}{xAxisEl}{yAxisEl}{tip}{leg}
            {yAxes.map((key, i) => <Area key={key} type="monotone" dataKey={key} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} strokeWidth={2} />)}
          </AreaChart>
        );
      case 'scatter':
        return (
          <ScatterChart margin={margin}>
            {grid}{xAxisEl}{yAxisEl}{tip}{leg}
            {yAxes.map((key, i) => (
              <Scatter key={key} name={key} data={chartData.map(d => ({ [xKey]: d[xKey], [key]: d[key] }))} fill={COLORS[i % COLORS.length]} />
            ))}
          </ScatterChart>
        );
      default:
        return (
          <LineChart data={chartData} margin={margin}>
            {grid}{xAxisEl}{yAxisEl}{tip}{leg}
            {yAxes.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        );
    }
  };

  return (
    <div data-testid="data-analysis-page">
      <div className="mb-6">
        <h1 className="heading-display">Data Analysis</h1>
        <p className="text-slate-500 mt-1">Analyze experimental data and create visualizations</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setActiveTab('growth')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'growth' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30 hover:border-slate-600/50'}`} data-testid="growth-tab">
          <TrendUp size={16} className="inline mr-2" /> Growth Curve
        </button>
        <button onClick={() => setActiveTab('csv')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'csv' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30 hover:border-slate-600/50'}`} data-testid="csv-tab">
          <Table size={16} className="inline mr-2" /> CSV Data
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Panel */}
        <div className="card-flat p-6">
          {activeTab === 'growth' ? (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-slate-100">Growth Curve Analysis</h3>
              <p className="text-sm text-slate-500">Enter time points and OD600 values separated by commas</p>
              <div>
                <Label className="text-xs text-slate-400">Time Points (hours)</Label>
                <Input value={growthData.time} onChange={(e) => setGrowthData({ ...growthData, time: e.target.value })} placeholder="0, 1, 2, 3, 4, 5, 6" className="input-field w-full mt-1" data-testid="time-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">OD600 Values</Label>
                <Input value={growthData.od} onChange={(e) => setGrowthData({ ...growthData, od: e.target.value })} placeholder="0.05, 0.1, 0.2, 0.4, 0.8, 1.2, 1.5" className="input-field w-full mt-1" data-testid="od-input" />
              </div>
              <Button onClick={handleGrowthAnalysis} className="btn-primary" disabled={loading} data-testid="analyze-growth-btn">
                {loading ? 'Analyzing...' : <><Lightning size={16} className="mr-2" /> Analyze</>}
              </Button>
              {growthResults && (
                <div className="mt-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700/30 space-y-2" data-testid="growth-results">
                  <h4 className="text-sm font-semibold text-slate-200">Results</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">Max Growth Rate:</span><span className="ml-2 font-mono text-slate-200">{growthResults.max_growth_rate} h⁻¹</span></div>
                    <div><span className="text-slate-500">Doubling Time:</span><span className="ml-2 font-mono text-slate-200">{growthResults.doubling_time ? `${growthResults.doubling_time} h` : 'N/A'}</span></div>
                    <div><span className="text-slate-500">Max OD:</span><span className="ml-2 font-mono text-slate-200">{growthResults.max_od}</span></div>
                    <div><span className="text-slate-500">Lag Phase End:</span><span className="ml-2 font-mono text-slate-200">{growthResults.lag_phase_end_time ? `${growthResults.lag_phase_end_time} h` : 'N/A'}</span></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-slate-100">CSV Data Visualization</h3>
              <p className="text-sm text-slate-500">Paste CSV data with headers in the first row</p>
              <div>
                <Label className="text-xs text-slate-400">CSV Data</Label>
                <Textarea value={csvData} onChange={(e) => setCsvData(e.target.value)} placeholder={"Time,Sample1,Sample2\n0,0.1,0.12\n1,0.2,0.25\n2,0.4,0.5"} className="input-field w-full mt-1 min-h-[180px] font-mono text-sm" data-testid="csv-input" />
              </div>
              <Button onClick={handleCSVVisualize} className="btn-primary" data-testid="load-csv-btn">
                <Table size={16} className="mr-2" /> Load Data
              </Button>
            </div>
          )}
        </div>

        {/* Chart Panel */}
        <div className="card-flat p-6">
          <h3 className="text-base font-semibold text-slate-100 mb-4">Visualization</h3>

          {/* Axis Controls */}
          {chartData && chartData.length > 0 && allHeaders.length > 0 && (
            <div className="space-y-3 mb-4 p-4 rounded-lg bg-slate-800/40 border border-slate-700/20" data-testid="axis-controls">
              {/* Chart type */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Chart Type</Label>
                <div className="flex gap-1.5">
                  {CHART_TYPES.map(ct => (
                    <button key={ct.value} onClick={() => setChartType(ct.value)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${chartType === ct.value ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-slate-700/30 text-slate-400 border border-slate-700/30 hover:text-slate-300'}`}
                      data-testid={`chart-type-${ct.value}`}
                    >
                      {ct.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* X axis selector */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">X Axis</Label>
                <div className="flex gap-2 items-center">
                  <select value={xAxis} onChange={e => setXAxis(e.target.value)} className="input-field text-sm flex-1" data-testid="x-axis-select">
                    {allHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <Input value={xLabel} onChange={e => setXLabel(e.target.value)} placeholder="X label" className="input-field text-sm w-32" data-testid="x-label-input" />
                </div>
              </div>

              {/* Y axes multi-select */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Y Axes (click to toggle)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {allHeaders.filter(h => h !== xAxis).map((h, i) => (
                    <button key={h} onClick={() => toggleYAxis(h)}
                      className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${yAxes.includes(h)
                        ? 'border text-white' : 'bg-slate-700/20 text-slate-500 border border-slate-700/30'}`}
                      style={yAxes.includes(h) ? { background: COLORS[i % COLORS.length] + '22', borderColor: COLORS[i % COLORS.length] + '55', color: COLORS[i % COLORS.length] } : {}}
                      data-testid={`y-axis-toggle-${h}`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <Input value={yLabel} onChange={e => setYLabel(e.target.value)} placeholder="Y axis label" className="input-field text-sm mt-2 w-48" data-testid="y-label-input" />
              </div>
            </div>
          )}

          {/* Chart */}
          {chartData && chartData.length > 0 && yAxes.length > 0 ? (
            <ResponsiveContainer width="100%" height={380}>
              {renderChart()}
            </ResponsiveContainer>
          ) : (
            <div className="h-[380px] flex items-center justify-center rounded-lg bg-slate-800/30 border border-slate-700/20">
              <div className="text-center text-slate-500">
                <ChartLine size={48} className="mx-auto mb-2 text-slate-600" />
                <p className="text-sm">Enter data and load to see visualization</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataAnalysisPage;
